import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  BarChart3, TrendingUp, Users, Building2, Target, Brain, Shield, 
  Zap, RefreshCw, ArrowRight, Info, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BackNavigation from "@/components/BackNavigation";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Interpretation band colors
const getBandColor = (band) => {
  switch (band) {
    case "Critical": return "bg-red-500";
    case "Emerging": return "bg-amber-500";
    case "Developing": return "bg-blue-500";
    case "Advanced": return "bg-emerald-500";
    default: return "bg-gray-500";
  }
};

const getBandTextColor = (band) => {
  switch (band) {
    case "Critical": return "text-red-600";
    case "Emerging": return "text-amber-600";
    case "Developing": return "text-blue-600";
    case "Advanced": return "text-emerald-600";
    default: return "text-gray-600";
  }
};

// Input force labels
const INPUT_FORCES = {
  HC: { label: "Human Capability", icon: Users, description: "Skills, knowledge, and potential of the workforce" },
  TS: { label: "Trust Structure", icon: Shield, description: "Organizational trust and relationship quality" },
  LE: { label: "Leadership Effectiveness", icon: Target, description: "Leadership quality and strategic direction" },
  OC: { label: "Organizational Coherence", icon: Building2, description: "Alignment of systems, processes, and culture" },
  OF: { label: "Opportunity Flow", icon: TrendingUp, description: "Access to growth, mobility, and advancement" },
  SP: { label: "Strategic Positioning", icon: Brain, description: "Market positioning and competitive advantage" }
};

// Output construct labels
const OUTPUT_CONSTRUCTS = {
  WA: { label: "Workforce Adaptivity", description: "Ability to adapt to change and new challenges" },
  WR: { label: "Workforce Resiliency", description: "Capacity to recover and maintain performance" },
  CV: { label: "Collective Value", description: "Combined value generated by the workforce" },
  QD: { label: "Quality Discipline", description: "Commitment to quality and continuous improvement" },
  WAut: { label: "Workforce Autonomy", description: "Independence and self-direction capability" },
  LC: { label: "Learning Curve", description: "Speed of learning and skill acquisition" }
};

// Final outcome labels
const FINAL_OUTCOMES = {
  SUS: { label: "Sustainability", description: "Long-term viability and environmental/social impact" },
  PROD: { label: "Productivity", description: "Output efficiency and effectiveness" },
  SCAL: { label: "Scalability", description: "Ability to grow and expand operations" },
  EM: { label: "Economic Mobility", description: "Financial growth and advancement potential" },
  ALIGN: { label: "Alignment", description: "Strategic and operational alignment" }
};

// Score display component
const ScoreCard = ({ score, label, interpretation, size = "md" }) => {
  const sizeClasses = {
    sm: "w-16 h-16 text-lg",
    md: "w-24 h-24 text-2xl",
    lg: "w-32 h-32 text-4xl"
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className={`${sizeClasses[size]} rounded-full ${getBandColor(interpretation)} flex items-center justify-center text-white font-bold shadow-lg`}>
        {score?.toFixed(1) || "—"}
      </div>
      <p className="mt-2 text-sm font-medium text-gray-700">{label}</p>
      <Badge className={`mt-1 ${getBandColor(interpretation)} text-white`}>{interpretation}</Badge>
    </div>
  );
};

// Progress bar with score
const ScoreProgress = ({ label, score, interpretation }) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-bold ${getBandTextColor(interpretation)}`}>{score?.toFixed(1)}</span>
        <Badge variant="outline" className={getBandTextColor(interpretation)}>{interpretation}</Badge>
      </div>
    </div>
    <Progress value={score} className="h-2" />
  </div>
);

export default function WAITDashboard() {
  const [activeTab, setActiveTab] = useState("assessment");
  const [isCalculating, setIsCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Input state
  const [inputs, setInputs] = useState({
    HC: 70,
    TS: 70,
    LE: 70,
    OC: 70,
    OF: 70,
    SP: 70
  });
  
  // Control variables
  const [controls, setControls] = useState({
    age: "",
    gender: "",
    education: "",
    ai_integration: 50,
    work_experience: ""
  });
  
  // Calculate WAIT scores
  const calculateScores = async () => {
    setIsCalculating(true);
    try {
      const response = await axios.post(`${API_URL}/api/wait/calculate`, {
        inputs: inputs,
        controls: {
          age: controls.age ? parseInt(controls.age) : null,
          gender: controls.gender || null,
          education: controls.education || null,
          ai_integration: controls.ai_integration,
          work_experience: controls.work_experience ? parseInt(controls.work_experience) : null
        },
        level: "individual"
      });
      setResult(response.data);
    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setIsCalculating(false);
    }
  };
  
  // Update input force
  const updateInput = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: value[0] }));
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="wait-dashboard">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">WAIT Scoring Engine</h1>
              <p className="text-white/80">Workforce AI Integration Theory Assessment</p>
            </div>
          </div>
          <p className="text-white/70 max-w-2xl">
            Calculate your Workforce Intelligence Index (WII) using the six input forces. 
            Receive detailed scores for output constructs and final outcome metrics.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="assessment">Assessment</TabsTrigger>
            <TabsTrigger value="results">Results</TabsTrigger>
          </TabsList>

          {/* Assessment Tab */}
          <TabsContent value="assessment" className="space-y-8">
            {/* Input Forces */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#991B1B]" />
                  Input Forces
                </CardTitle>
                <CardDescription>
                  Rate each of the six input forces on a scale of 0-100
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(INPUT_FORCES).map(([key, { label, icon: Icon, description }]) => (
                  <div key={key} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#1E3A8A]" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">{label} ({key})</Label>
                          <p className="text-xs text-gray-500">{description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-[#1E3A8A]">{inputs[key]}</span>
                        <span className="text-sm text-gray-500">/100</span>
                      </div>
                    </div>
                    <Slider
                      value={[inputs[key]]}
                      onValueChange={(value) => updateInput(key, value)}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Control Variables */}
            <Card>
              <CardHeader>
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="w-5 h-5 text-gray-500" />
                      Control Variables
                    </CardTitle>
                    <CardDescription>Optional demographic and context data</CardDescription>
                  </div>
                  {showDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </CardHeader>
              {showDetails && (
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Age</Label>
                      <Input
                        type="number"
                        placeholder="Enter age"
                        value={controls.age}
                        onChange={(e) => setControls(prev => ({ ...prev, age: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Gender</Label>
                      <Select value={controls.gender} onValueChange={(v) => setControls(prev => ({ ...prev, gender: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                          <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Education Level</Label>
                      <Select value={controls.education} onValueChange={(v) => setControls(prev => ({ ...prev, education: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select education" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high_school">High School</SelectItem>
                          <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                          <SelectItem value="masters">Master's Degree</SelectItem>
                          <SelectItem value="doctorate">Doctorate</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Work Experience (years)</Label>
                      <Input
                        type="number"
                        placeholder="Years of experience"
                        value={controls.work_experience}
                        onChange={(e) => setControls(prev => ({ ...prev, work_experience: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <div className="flex justify-between">
                        <Label>AI Integration Level</Label>
                        <span className="text-sm font-medium">{controls.ai_integration}/100</span>
                      </div>
                      <Slider
                        value={[controls.ai_integration]}
                        onValueChange={(v) => setControls(prev => ({ ...prev, ai_integration: v[0] }))}
                        max={100}
                        step={1}
                      />
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Calculate Button */}
            <div className="flex justify-center">
              <Button 
                size="lg" 
                className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white px-12 h-14"
                onClick={calculateScores}
                disabled={isCalculating}
              >
                {isCalculating ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    Calculate WII Score
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="space-y-8">
            {result ? (
              <>
                {/* WII Score Hero */}
                <Card className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white border-0">
                  <CardContent className="py-12">
                    <div className="flex flex-col items-center">
                      <p className="text-white/70 mb-4">Workforce Intelligence Index</p>
                      <ScoreCard 
                        score={result.wii_score} 
                        label="" 
                        interpretation={result.interpretations.WII}
                        size="lg"
                      />
                      <p className="mt-6 text-center max-w-md text-white/80">
                        Your WII score reflects your overall workforce intelligence based on the six input forces.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Input Forces Results */}
                <Card>
                  <CardHeader>
                    <CardTitle>Input Forces Breakdown</CardTitle>
                    <CardDescription>Your scores across the six input dimensions</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(result.raw_inputs).map(([key, score]) => (
                      <ScoreProgress 
                        key={key}
                        label={INPUT_FORCES[key]?.label || key}
                        score={score}
                        interpretation={score >= 80 ? "Advanced" : score >= 60 ? "Developing" : score >= 40 ? "Emerging" : "Critical"}
                      />
                    ))}
                  </CardContent>
                </Card>

                {/* Output Constructs */}
                <Card>
                  <CardHeader>
                    <CardTitle>Output Constructs</CardTitle>
                    <CardDescription>Derived capabilities from your input forces</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {Object.entries(result.output_constructs).map(([key, score]) => (
                        <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className={`w-16 h-16 mx-auto rounded-full ${getBandColor(result.interpretations.constructs[key])} flex items-center justify-center text-white font-bold text-xl`}>
                            {score.toFixed(0)}
                          </div>
                          <p className="mt-2 text-sm font-medium text-gray-800">{OUTPUT_CONSTRUCTS[key]?.label || key}</p>
                          <Badge className={`mt-1 ${getBandColor(result.interpretations.constructs[key])} text-white text-xs`}>
                            {result.interpretations.constructs[key]}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Final Outcomes */}
                <Card>
                  <CardHeader>
                    <CardTitle>Final Outcome Metrics</CardTitle>
                    <CardDescription>Projected organizational outcomes based on your WII</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(result.final_outcomes).map(([key, score]) => (
                      <ScoreProgress 
                        key={key}
                        label={FINAL_OUTCOMES[key]?.label || key}
                        score={score}
                        interpretation={result.interpretations.outcomes[key]}
                      />
                    ))}
                  </CardContent>
                </Card>

                {/* Weights Used */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="w-5 h-5 text-gray-500" />
                      Calculation Methodology
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-4">
                        <strong>Cobb-Douglas Function:</strong> WII = HC<sup>α₁</sup> × TS<sup>α₂</sup> × LE<sup>α₃</sup> × OC<sup>α₄</sup> × OF<sup>α₅</sup> × SP<sup>α₆</sup>
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Phase 1 Weights:</strong> Equal weighting (α = 1/6 ≈ 0.167 for each input force)
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Results Yet</h3>
                  <p className="text-gray-500 mb-4">Complete the assessment to see your WAIT scores</p>
                  <Button onClick={() => setActiveTab("assessment")} variant="outline">
                    Go to Assessment
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Interpretation Guide */}
      <section className="bg-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Interpretation Guide</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="font-semibold">Critical (0-39)</span>
              </div>
              <p className="text-sm text-gray-600">Requires immediate attention and intervention</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 rounded-full bg-amber-500"></div>
                <span className="font-semibold">Emerging (40-59)</span>
              </div>
              <p className="text-sm text-gray-600">Foundation building stage with room for growth</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="font-semibold">Developing (60-79)</span>
              </div>
              <p className="text-sm text-gray-600">Solid foundation with ongoing improvement</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
                <span className="font-semibold">Advanced (80-100)</span>
              </div>
              <p className="text-sm text-gray-600">High performance and excellence demonstrated</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
